import { IAdvanceStageResult, IProductContextCase } from 'src/app/models/case';
import { EventEmitter, TemplateRef, Type } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { InterviewDefaultDataProviderService } from '../instances/data-providers/interview-default-data-provider.service';

import { VariableStringParserService } from '../services/evaluation-formatters/variable-string-parser.service';
import { InterviewHandlerService } from '../services/interview-handler.service';
import { BaseVariableControl, IVariableControl } from '../services/variable-controls/_base-control';
import { CompletionProcess, DisplayMetadata, IDisplayMetadata } from './template.models';
import { ICreateControlOptions } from '../template/grid-template/grid-template.models';
import { Member } from 'src/app/models/member';
import { IInterviewDataProvider } from '../instances/data-providers/data-providers.models';


export enum InterviewOrchestrationTypes {
    CustomUHC = 'CustomUHC'
}

export enum ExecutionMode {
    Standard = 'Standard',
    Test = 'Test',
    RequestStructure = 'RequestStructure'
}

export enum EvaluationSeverityMode {
    Lineant = 'Lineant',
    Strict = 'Strict'
}


export enum AssessmentMode {
    Primary = 'Primary',
    Secondary = 'Secondary'
}

export class InterviewContextEvents {

    lifeCycleEvent$ = new Subject<IInterviewEvent>();

    onValueChanged$ = new Subject<BaseVariableControl>();

    onTemplateValueChanged$: Subscription;

    evaluationResult$ = new BehaviorSubject<IEvaluationResult>(null);

    private _evaluationSeverity$ = new BehaviorSubject<EvaluationSeverityMode>(EvaluationSeverityMode.Lineant);
    evaluationSeverity$ = this._evaluationSeverity$.asObservable();

    forceFinish$ = new EventEmitter<any>();

    completionProcess$ = new BehaviorSubject<CompletionProcess>(null);


    constructor(private handler: IInterviewHandler) {
        setTimeout(async () => {
            await this.initializeEvaluationSeverityMode();
        });
    }

    setSeverity(evaluationSeverity: EvaluationSeverityMode) {
        this._evaluationSeverity$.next(evaluationSeverity);
    }

    getSeveritySync() {
        return this._evaluationSeverity$.value;
    }

    async initializeEvaluationSeverityMode() {
        const evaluationServerity = EvaluationSeverityMode.Strict;
        this.setSeverity(evaluationServerity);
    }

    setOnValueChanged(callback: (handler: BaseVariableControl) => void) {
        this.onTemplateValueChanged$ = this.handler.onValueChanged.subscribe(callback);
    }

    clear() {
        this.evaluationResult$.next(null);
        this.completionProcess$.next(null);

        if (!!this.onTemplateValueChanged$ && !this.onTemplateValueChanged$.closed) {
            this.onTemplateValueChanged$.unsubscribe();
        }
        this.onTemplateValueChanged$ = null;
    }
}




export enum InterviewEventType {
    InitialLoad = 'initial-load',
    Initialized = 'initialized',
    Finished = 'finished',
    Next = 'next',
    Previous = 'prev',
    Postback = 'postback',
    Goto = 'goto',
    Forward = 'forward',
    Evaluation = 'evaluation',
    Finish = 'finish',
    Done = 'Done',
    Refresh = "Refresh"
}

export enum InterviewType {
    Community = 'Community',
    Medicaid = 'Medicaid',
    LIS = 'LIS',
    SNAP = 'SNAP',
    Veteran = 'VA',
    RPC = 'RPC',
    Custom = 'Custom',
    MSP = 'MSP',
    ABD = 'ABD',
    Document = 'Document'
}


export enum EntityType {
    Case = 'Case',
    Orchestration = 'Orchestration',
    Telephony = 'Telephony',
    Document = 'Document'
}

export interface IVariableEvaluationModel {
    name: string;
    value?: any;
}

export interface InterviewMetadataItem {
    key: string;
    displayName: string;
    value: any;
    description: string;
    category: string;
}


export interface IEvaluationResult {
    pages: any[];
    categories: ICategoryResult[];
    variables: IVariable[];
    currentPage: ICurrentPage;
    extraData: any;
    summaryMessages: any;
    interviews: IInterviewResult[];
    interviewStack: string[][];
    hasError: boolean;
    metadata: InterviewMetadataItem[];
    hasEvaluationSources: boolean;
    externalId?: string;
    interviewErrors: any[];
}

export interface IEvaluationRequest {
    packageName: string,
    version: string,
    request: {
        variables?: IVariableEvaluationModel[];
        pagingInfo?: PagingInfo;
        force?: boolean;
        initialize: boolean;
        executionMode: ExecutionMode;
        mode?: AssessmentMode;
        evaluationSeveryMode?: EvaluationSeverityMode;
    }
}
export class EvaluationResult implements IEvaluationResult {
    pages: EvaluationResultPage[];
    categories: ICategoryResult[];
    variables: IVariable[];
    currentPage: ICurrentPage;
    extraData: any;
    summaryMessages: any;
    interviews: IInterviewResult[];
    interviewStack: string[][];
    metadata: InterviewMetadataItem[];
    hasEvaluationSources = false;

    interviewErrors: any[];

    get currentPageVariables() {
        return this.currentPage.variables;
    }


    constructor(input: IEvaluationResult) {
        const internalInput = input || {} as IEvaluationResult;
        this.pages = (internalInput.pages || []);
        this.interviews = (internalInput.interviews || []).map(interview => ({
            name: interview.name,
            displayName: interview.displayName,
            evaluationStatus: interview.evaluationStatus,
            completion: interview.completion,
            extraData: internalInput.extraData,
            summaryMessages: internalInput.summaryMessages,
            hasEvaluationErrors: interview.hasEvaluationErrors,
            variables: interview.variables,
            disqualificationReasons: interview.disqualificationReasons,
            qualificationResultItems: EvaluationResult.sortDisqualificationResons(interview.qualificationResultItems)
        } as IInterviewResult));

        this.variables = (internalInput.variables || []).map(variableItem => new Variable(variableItem));
        this.categories = (internalInput.categories || []).map(categoryItem => new CategoryResult(categoryItem));
        this.currentPage = new CurrentPage((internalInput.currentPage || {} as ICurrentPage));
        this.interviewStack = internalInput.interviewStack;

        this.extraData = internalInput.extraData;
        this.summaryMessages = internalInput.summaryMessages;
        this.metadata = internalInput.metadata;
        this.hasEvaluationSources = !!this.categories && this.categories && this.categories.some(cat => !!cat.evaluationSources && cat.evaluationSources.length > 0);
    }

    static sortDisqualificationResons(qualificationTypes: any): TypedQualificationReason[] {
        return (qualificationTypes as any[]).sort((a, b) => a.disqualifications.length ? 1 : 0);
    }

    get hasError() {
        return this.pages.some(page => page.validationErrors && page.validationErrors.length > 0);
    }
}



// tslint:disable-next-line: no-empty-interface
export interface IInstantiationConfig {

}

export interface EvaluationResultPage {
    categoryName: string;
    pageNumber: number;
    isCurrent: boolean;
    validationErrors: EvaluationError[];
    disqualificationReasons: any[];
}

export interface EvaluationError {
    message: string;
    code: string;
    attemptedValue: any;
    propertyName: string;
    resourceName: string;
    severity: number;
}

export interface IVariable {
    name: string;
    description: string;
    script: string;
    forcePostback: boolean;
    icon: string;
    severity: string;

    value?: any;
    display: boolean;
    isHidden: boolean;
    dependOn: string[];
    dependencies: string[];
    validationErrors?: IErrorData[];
    disqualificationReasons: any[];
    order: number;
    displayName: string;
    categoryName: string;

    type?: string;
    hasValue?: string;
    displayMetadata: IDisplayMetadata;
    width: string;
    scriptFormatted: string;
    displayNameFormatted: string;
    descriptionFormatted: string;
    hasDependencies: boolean;
    staticLabel: boolean;
    pageNumber: number;

    verificationDetails: any;
    metadata: any;

    displayProperties: any;

    format(handler: IInterviewHandler, variableStringParserService: VariableStringParserService): void;
}

export class Variable implements IVariable {
    name: string;
    description: string;
    script: string;
    forcePostback: boolean;
    icon: string;
    severity: string;
    value?: any;
    display: boolean;
    isHidden: boolean;
    dependOn: string[];
    dependencies: string[];
    validationErrors?: IErrorData[];
    disqualificationReasons: any[];
    order: number;
    displayName: string;
    categoryName: string;
    type?: string;
    hasValue?: string;
    displayMetadata: IDisplayMetadata;
    width: string;
    scriptFormatted: string;
    displayNameFormatted: string;
    descriptionFormatted: string;
    staticLabel: boolean;
    pageNumber: number;

    verificationDetails: any;
    metadata: any;
    displayProperties: any;

    get hasDependencies() {
        return this.dependencies && this.dependencies.length > 0;
    }

    constructor(variable: Partial<IVariable>) {
        this.name = variable.name;
        this.description = variable.description;
        this.script = variable.script;
        this.icon = variable.icon;
        this.severity = variable.severity;
        this.forcePostback = variable.forcePostback;
        this.value = variable.value;
        this.display = variable.display;
        this.isHidden = variable.isHidden;
        this.dependOn = variable.dependOn;
        this.dependencies = variable.dependencies;
        this.validationErrors = variable.validationErrors;
        this.disqualificationReasons = variable.disqualificationReasons;
        this.order = variable.order;
        this.displayName = variable.displayName;

        this.metadata = variable.metadata || {};
        this.verificationDetails = variable.verificationDetails;

        this.categoryName = variable.categoryName;
        this.type = variable.categoryName;
        this.hasValue = variable.hasValue;
        this.displayMetadata = variable.displayMetadata ? new DisplayMetadata(variable.displayMetadata) : null;
        this.displayProperties = variable.displayProperties;


        this.width = variable.width;
        this.staticLabel = ['buttongroup', 'boolean', 'nullableboolean'].some(template => template === (this.displayMetadata && this.displayMetadata.template));
        this.pageNumber = variable.pageNumber;
    }


    format(handler: IInterviewHandler, variableStringParserService: VariableStringParserService, options?: IFormatVariableOptions) {

        this.scriptFormatted = variableStringParserService.process(this.script);
        this.displayNameFormatted = variableStringParserService.process(this.displayName);
        this.descriptionFormatted = variableStringParserService.process(this.description);

        if (this.displayMetadata) {

            let template = this.displayMetadata && this.displayMetadata.template;
            template = (template || 'input').toLowerCase();
            const handlerType = handler.controlMapping[template];
            const control = new handlerType();
            control.variable = this;
            control.handler = handler;

            control.form = options?.formOptions?.targetForm;

            this.displayMetadata.formControl = control;

            this.displayMetadata.formControl.createReactiveControl(true);
        }
    }
    //#endregion


}

export interface IVariableControlConfig {
    showUI: boolean;
    onValueChanged: (variable: IVariable) => void;
}
export interface IErrorData {
    message: string;
    code: string;
    attemptedValue: any;
    propertyName: string;
    resourceName: string;
    severity: string;
}

export interface IInterviewWrapper {
    interviewHandler: InterviewHandlerService;
}

export interface IInterviewSettingsStyle {
    zIndex: number;
}

export interface IInterviewSettings {
    package?: string;
    version?: string;
    type?: string;

    pageSize?: number;

    executionMode?: ExecutionMode;
    mode?: AssessmentMode,
    evaluationSeverityMode?: EvaluationSeverityMode,

    summaryComponentId?: string;
    byPassToSummary?: boolean;

    dataProvider?: IInterviewDataProvider;
    extraContextData?: any;

    contextDataCallback?: (settings?: any) => Promise<any>;

    //#region Flags
    hideNavigation?: boolean;
    hideCategories?: boolean;
    showInterviewMetadata?: any;
    hideOptions?: boolean;
    hideProducts?: boolean;
    hideFinishButton?: boolean;
    finishButtonLabel?: string;
    finishButtonIcon?: string;
    //#endregion

    style?: IInterviewSettingsStyle;

    optionsTemplate?: TemplateRef<any>;
    optionsLeftTemplate?: TemplateRef<any>;
    optionsCenterTemplate?: TemplateRef<any>;
    optionsRightTemplate?: TemplateRef<any>;
}

export interface ICurrentPage {
    categoryName: string;
    displayName: string;
    index: number;
    pageSize: number;
    variables: IVariable[];
    hasError: boolean;
    isLastPage: boolean;
    forceStop: boolean;
}

export class CurrentPage implements ICurrentPage {
    categoryName: string;
    displayName: string;
    index: number;
    pageSize: number;
    variables: IVariable[];
    isLastPage: boolean;
    forceStop: boolean;

    get hasError() {
        return this.variables.some(variable => variable.validationErrors && variable.validationErrors.length > 0);
    }

    constructor(input: ICurrentPage) {
        this.categoryName = input.categoryName;
        this.displayName = input.displayName;
        this.index = input.index;
        this.pageSize = input.pageSize;
        this.isLastPage = input.isLastPage;
        this.forceStop = input.forceStop;

        this.variables = (input.variables || []).map(variableItem => new Variable(variableItem));
    }
}

export interface IInterviewResult {
    name: string;
    displayName: string;
    evaluationStatus: string;
    completion: number;
    extraData: any;
    summaryMessages: any;
    hasEvaluationErrors: boolean;
    variables: IVariableReference[];
    disqualificationReasons: IDisqualificationReason[];
    qualificationResultItems?: TypedQualificationReason[];
    evaluationSeverityMode: EvaluationSeverityMode;
}

export interface ITypeQualificationReason {
    type: string;
    displayName: string;
    disqualifications: IDisqualificationReason[];
    status: string;
}

export class TypedQualificationReason implements ITypeQualificationReason {
    type: string;
    displayName: string;
    disqualifications: IDisqualificationReason[];
    status: string;

    static qualificationBundle(bundle: TypedQualificationReason[]) {
        return bundle.reduce((prev, current) => [...prev, ...current.disqualifications], []);
    }
}

export interface IDisqualificationReason {
    sourceName: string;
    sourceType: string;
    sourceDisplayName: string;
    severity: string;
    message: string;
    resultCode: string;
    code: string;
    type: string;
    metadata: string;
}

export interface ICategoryResult {
    displayName: string;
    name: string;
    pages: IPageResult[];
    variables: IVariable[];
    firstPageIndex: number;
    hasValidationErrors: boolean;
    evaluationSources: IVariableEvaluationSource[];
}


export interface IVariableEvaluationSource {
    name: string;
    value: string;
    description: string;
}

export interface IPageResult {
    pageNumber: number;
    categoryIndex: number;
}

export interface IVariableReference {
    name: string;
}

export interface ICategoryPage {
    categoryIndex: number;
    pageNumber: number;
}


export interface PagingInfo {
    pageIndex?: number;
    pageSize?: number;
    action?: InterviewEventType;
}

export interface InitializationRequest {
    entityId: string;
    entityType: string;
    assessmentType: string;
    mode: string;
}

export interface IFinalizeInterviewRequest {
    packageName: string,
    version: string,
    request: {
        tenantId?: string;
        memberId?: string;
        evaluationResult: IEvaluationResult;
        entityId?: string;
        entityType?: EntityType;
    }
}



export class CategoryResult implements ICategoryResult {
    firstPageIndex: number;
    displayName: string;
    name: string;
    pages: IPageResult[];
    variables: IVariable[];
    hasValidationErrors: boolean;
    evaluationSources: IVariableEvaluationSource[];



    constructor(input: Partial<ICategoryResult>) {
        Object.assign(this, input);
        this.pages = (input.pages || []).map(pageItem => (pageItem as ICategoryPage));
        this.variables = (input.variables || []).map(variableItem => new Variable(variableItem));
    }
}


export interface IInterviewEvent {
    type: InterviewEventType;
    handler: IInterviewHandler;
    payload?: any;
}


export interface IInterviewHandler {
    onButtonClick: EventEmitter<IVariable>;
    onValueChanged: EventEmitter<BaseVariableControl>;
    readonly isPrimary: any;
    initialized: boolean;
    settings: IInterviewSettings;
    variableStringParserService: VariableStringParserService;
    fmBuilder: UntypedFormBuilder;
    form: UntypedFormGroup;
    controlMapping: { [key: string]: Type<IVariableControl> };
    events: InterviewContextEvents;
    completed: boolean;
    processingFinalization: boolean;
    showErrorsAllowed: boolean;
    previous(): void;
    initialize(settings: IInterviewSettings);
    evaluate(action: InterviewEventType, variables?: IVariableEvaluationModel[]);
    toggleSeverityMode();
    clear();
    prepareVariablesToEvaluate(): IVariableEvaluationModel[];

    finish(): any;
    finalizev2(extraContextData?: any): Promise<any>;
    finalize(extraContextData?: any): Promise<any>;
}

export enum InterviewAlternativeView {
    InterviewVariables = 'InterviewVariables'
}


export interface IInterviewFinalizer {
    finalizeInterview: (interviewHandler: InterviewHandlerService, interviewDataProvider: InterviewDefaultDataProviderService) => Promise<IAdvanceStageResult | void>;
}

export interface IFormatVariableOptions {
    formOptions: ICreateControlOptions;
}



export interface InterviewGlobalMetadata {
    categoriesFunc?: () => Observable<any[]>;
    extraCompletionProcess?: boolean;
    executionMode?: ExecutionMode;
    mode: AssessmentMode;

    interview?: IAvailableInterview;


    entityType: EntityType;
    entityId?: string;
    tenantId?: string;

    $case?: IProductContextCase;


    pageSize: number;
    hideProducts?: boolean;
    hideOptions?: boolean;
    hideFinishButton?: boolean;
    finishButtonLabel?: string;

    hideNavigation?: boolean;
    hideCategories?: boolean;
    showInterviewMetadata?: boolean;
    interviewContextFunc?: () => void;
    evaluationSeveryMode?: EvaluationSeverityMode;
    member?: Member
    byPassToSummary?: boolean;
}


export interface IAvailableInterview {
    interviewId: string;
    endpoint: string;
    interviewType: string;
    assessmentType: string;
    state: string;
    interviewApiBaseUrl: string;
}


export interface IPersistedEvaluationResult {
    extraData: any;
    progress: {
        completion: number;
        lastUpdatingUser: string;
        lastUpdatingUserObjectId: string;
    };
    result: IPersistedEvaluationResultData;
    variables: IVariable[];
}

export interface IPersistedEvaluationResultData {
    status: string;
    reasons: TypedQualificationReason[];// IPersistedEvaluationResultReasons[];
}

export interface IPersistedEvaluationResultReasons {

}
